import { type FetchOptions, type FetchResponse } from 'ofetch';
import { apiUrl, isExternalRoute, normalisePath } from '~/utils/routes';

/**
 * Return the decoded token cookie for use in $fetch requests.
 */
const decodedToken = (): string => {
  // Extract the token cookie from the cookies string
  const tokenCookie = document.cookie
    .split('; ')
    .find((row) => row.startsWith('XSRF-TOKEN='))
    ?.split('=')[1];

  // Decode the token
  // @ts-ignore
  return decodeURIComponent(tokenCookie);
};

/**
 * Set user data in the Pinia store using response data.
 *
 * @param response
 */
const setAuthData = (
  response: FetchResponse<any> | FetchResponse<ResponseType>,
): void => {
  // Skip the following checks for 204 responses as they have no content
  if (response.status === 204) {
    return;
  }

  // Skip the following checks for non JSON responses as they never contain a user object
  if (response.headers.get('content-type') !== 'application/json') {
    return;
  }

  // Only update from responses that actually include a user. Treating an
  // absent `user` field as "session ended" is too broad — error bodies and
  // endpoints that simply don't echo the session user would wipe auth state
  // and trigger a redirect chain. Real session loss is conveyed by 401 and is
  // handled in `apiRequest`'s catch block.
  if (!response._data.user) {
    return;
  }

  const auth = useAuthStore() as any;

  // Extract the user from the response if present
  const { user } = response._data;

  // Extract the user's relations that have alternate namings to the API
  const { stores } = user;

  // Delete the user's API named relations from the response data
  delete user.stores;

  // Set extracted product relations to the product
  user.sales_channels = stores;

  // Set the user as the auth user
  auth.user = user;
};

/**
 * Set partner data in the common Pinia store using response data.
 *
 * @param response
 */
const setPartnerData = (
  response: FetchResponse<any> | FetchResponse<ResponseType>,
): void => {
  // Skip the following checks for 204 responses as they have no content
  if (response.status === 204) {
    return;
  }

  // Skip the following checks for non JSON responses as they never contain a partner object
  if (response.headers.get('content-type') !== 'application/json') {
    return;
  }

  // If the response contains a partner object
  if (response._data.partner) {
    // Update the partner set on the common Pinia store
    (useCommonStore() as any).partner = response._data.partner;
  }
};

/**
 * Set account data in the Pinia store using response data.
 *
 * @param response
 */
const setAccountData = (
  response: FetchResponse<any> | FetchResponse<ResponseType>,
): void => {
  // Skip 402 responses to prevent error response data from overwriting account state
  if (response.status === 402) {
    return;
  }

  // Skip the following checks for 204 responses as they have no content
  if (response.status === 204) {
    return;
  }

  // Skip the following checks for non JSON responses as they never contain an account object
  if (response.headers.get('content-type') !== 'application/json') {
    return;
  }

  // Only update from responses that actually include an account — an absent
  // `account` field on a normal response (e.g. an error body) is not a signal
  // that the user has lost their account; it just means the endpoint didn't
  // echo it. Account loss is handled via 401/402 in `apiRequest`'s catch.
  if (!response._data.account) {
    return;
  }

  const accountsStore = useAccountStore() as any;

  accountsStore.account = response._data.account;
};

/**
 * Set redeem location data in the Pinia store using response data.
 *
 * @param response
 */
const setRedeemLocationData = (
  response: FetchResponse<any> | FetchResponse<ResponseType>,
): void => {
  // Skip the following checks for 204 responses as they have no content
  if (response.status === 204) {
    return;
  }

  // Skip the following checks for non JSON responses as they never contain a user object
  if (response.headers.get('content-type') !== 'application/json') {
    return;
  }

  const terminalStore = useTerminalStore() as any;

  if (response._data.redeem_location) {
    // Set the redeem location from the response if present
    terminalStore.redeemLocation = response._data.redeem_location;
  } else {
    // Extract the API request path
    const path = response.url.replace(apiUrl(), '');

    // If the path does not begin with "/terminal"
    if (!path.startsWith('/terminal')) {
      // Leave the redeem location unchanged
      return;
    }

    // A redeem location is only attached once the request clears the redeem
    // location middleware, so its absence on other error responses (403/422/5xx)
    // says nothing about the session's redeem location — clearing it there
    // also drops the Redeem-Location-ID header from every following request,
    // wedging the terminal until remount. Only a 401 (session lost) clears.
    if (response.status !== 401) {
      // Leave the redeem location unchanged
      return;
    }

    // Otherwise there is no redeem location
    terminalStore.redeemLocation = null;
  }
};

/**
 * Attach Account-ID and Redeem-Location-ID headers to requests.
 *
 * @param options
 */
const setHeaders = (options: FetchOptions): FetchOptions => {
  const accountsStore = useAccountStore();
  const terminalStore = useTerminalStore();
  options.headers = options.headers ?? {};

  options.headers = {
    ...options.headers,
    Accept: 'application/json',

    // Using the decoded token
    'X-XSRF-TOKEN': decodedToken(),
  };

  // If an account is set
  if ((accountsStore as any).account) {
    options.headers = {
      ...options.headers,
      // Attach the Account-ID header to requests
      'Account-ID':
        (useAuthStore() as any).newAccountIdHeader ??
        (accountsStore as any).account.id,
    };
  }

  // If a redeem location is set
  if ((terminalStore as any).redeemLocation) {
    options.headers = {
      ...options.headers,
      // Attach the Redeem-Location-ID header to requests
      'Redeem-Location-ID': (terminalStore as any).redeemLocation.id,
    };
  }

  // If a sales channel is set on the terminal Pinia store
  if ((terminalStore as any).salesChannel) {
    options.headers = {
      ...options.headers,
      // Attach the store-hostname header to requests
      'store-hostname': `${(terminalStore as any).salesChannel.subdomain}.${(useCommonStore() as any).partner.store_root_domain}`,
    };
  }

  return options;
};

/**
 * Construct an API request using the API base URL environment variable.
 *
 * @param path
 * @param method
 * @param body
 * @param config
 * @param headers
 */
const apiRequest = async (
  path: string,
  method: 'GET' | 'PATCH' | 'POST' | 'DELETE',
  body: Object | null,
  config = {},
): Promise<any> => {
  // Normalise the path
  const normalisedPath = normalisePath(path);

  // Snapshot isSending at call start to track this request
  const commonStore = useCommonStore();
  const shouldTrack = commonStore.isSending;

  if (shouldTrack) {
    commonStore.sendingCount++;
  }

  try {
    // Execute the request
    return await $fetch(`${apiUrl()}${normalisedPath}`, {
      method,
      body,
      // Including cookies
      credentials: 'include',
      // With Account-ID header where appropriate
      async onRequest({ options }) {
        // @ts-ignore
        options = setHeaders(options);
      },
      // Checking for partner, user, account, and redeem location data in the response
      async onResponse({ response }) {
        setPartnerData(response);
        setAuthData(response);
        setAccountData(response);
        setRedeemLocationData(response);
      },
      ...config,
    });
  } catch (error: any) {
    // If the request returns 401
    if (error.statusCode === 401) {
      // The user is not logged in
      (useAuthStore() as any).user = null;

      // If the current route is not an external route
      if (!isExternalRoute()) {
        // Redirect to the login page with the current route as redirect
        await navigateTo({
          name: 'auth-login',
          replace: true,
          query: { redirect: useRoute().fullPath },
        });
      }

      // Otherwise, throw the error as normal
      throw error;
    }

    // If the request returns 402
    if (error.statusCode === 402) {
      const errorType = error.data?.error;

      // If the account is suspended
      if (errorType === 'account_suspended') {
        // Update the account status in the store so the middleware allows the suspended page
        const accountStore = useAccountStore();

        if (accountStore.account) {
          accountStore.account.status = 'suspended';
        }

        // Redirect to the suspended page
        await navigateTo('/account-suspended', { replace: true });

        throw error;
      }

      // If the account is restricted
      if (errorType === 'account_restricted') {
        // Show a toast notification
        notifyError(
          'Action not allowed',
          'This account is restricted. Write operations are disabled.',
        );

        throw error;
      }

      // If the account is pending deletion
      if (errorType === 'account_pending_deletion') {
        const accountStore = useAccountStore();

        if (accountStore.account) {
          accountStore.account.status = 'pending_deletion';
        }

        await navigateTo('/account-pending-deletion', { replace: true });

        throw error;
      }
    }

    throw error;
  } finally {
    if (shouldTrack) {
      commonStore.sendingCount--;
      if (commonStore.sendingCount <= 0) {
        commonStore.sendingCount = 0;
        // Defer the reset so sequential awaited calls can re-increment before the check
        setTimeout(() => {
          if (commonStore.sendingCount <= 0) {
            commonStore.sendingCount = 0;
            commonStore.isSending = false;
          }
        }, 0);
      }
    }
  }
};

/**
 * Construct an API GET request using the API base URL environment variable.
 *
 * @param path
 * @param config
 */
export const apiGet = async (path: string, config = {}): Promise<any> =>
  apiRequest(path, 'GET', null, config);

/**
 * Construct an API POST request using the API base URL environment variable.
 *
 * @param path
 * @param body
 * @param config
 */
export const apiPost = async (
  path: string,
  body = {},
  config = {},
): Promise<any> => apiRequest(path, 'POST', body, config);

/**
 * Construct an API PATCH request using the API base URL environment variable.
 *
 * @param path
 * @param body
 * @param config
 */
export const apiPatch = async (
  path: string,
  body = {},
  config = {},
): Promise<any> => apiRequest(path, 'PATCH', body, config);

/**
 * Construct an API DELETE request using the API base URL environment variable.
 *
 * @param path
 * @param config
 */
export const apiDelete = async (path: string, config = {}): Promise<any> =>
  apiRequest(path, 'DELETE', null, config);

/**
 * Construct an API request for downloading files with streaming support.
 * Returns the raw Response object to allow streaming.
 *
 * @param path The API endpoint path
 * @param options Additional fetch options
 * @returns Promise that resolves to a Response object
 */
export const apiDownloadStream = async (
  path: string,
  options: RequestInit = {},
): Promise<Response> => {
  // Normalize the path
  const normalizedPath = normalisePath(path);

  try {
    // Create fetch options with proper headers
    const fetchOptions: RequestInit = {
      method: 'GET',
      credentials: 'include',
      headers: {},
      ...options,
    };

    // Set headers (authentication, account, etc.)
    fetchOptions.headers = setHeaders(fetchOptions).headers;

    // Override Accept header for file downloads if not specified
    // Using type assertion to access headers safely
    const headers = options.headers as Record<string, string> | undefined;
    if (!headers?.Accept) {
      fetchOptions.headers = {
        ...fetchOptions.headers,
        Accept: 'text/csv,application/pdf,application/octet-stream',
      };
    }

    // Execute the request using native fetch to get the raw response
    const response = await fetch(`${apiUrl()}${normalizedPath}`, fetchOptions);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  } catch (error: any) {
    // If the request returns 401
    if (error.status === 401 || error.message?.includes('401')) {
      // The user is not logged in
      (useAuthStore() as any).user = null;

      // If the current route is not an external route
      if (!isExternalRoute()) {
        // Redirect to the login page with the current route as redirect
        await navigateTo({
          name: 'auth-login',
          replace: true,
          query: { redirect: useRoute().fullPath },
        });
      }
    }

    // Rethrow the error
    throw error;
  }
};
